import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Input, Popover, Tag,
} from 'antd';
import {
  CloseCircleFilled, CloseOutlined,
} from '@ant-design/icons';

const SEPARATORS_REGEX = /[\s,，、/;；]+/;

/**
 * 解析从 Excel 或其他表格复制出来的批量查询值。
 * Excel 单列复制通常会带换行或制表符，手工输入时也兼容逗号、分号和斜杠。
 */
export const parseBatchSearchValues = (text) => {
  if (typeof text !== 'string' || !text.trim()) return [];

  return [...new Set(text
    .split(SEPARATORS_REGEX)
    .map((value) => value.trim())
    .filter(Boolean))];
};

const hasMultipleBatchValues = (text) => typeof text === 'string'
  && text.split(SEPARATORS_REGEX).filter((value) => value.trim()).length >= 2;

const serializeValues = (values) => values.join(';');

const sameValues = (left, right) => left.length === right.length
  && left.every((value, index) => value === right[index]);

const BatchSearchInput = ({
  value,
  onChange,
  placeholder,
  label,
  buttonLabel,
  onSearch,
  style,
}) => {
  const [batchValues, setBatchValues] = useState([]);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [textAreaValue, setTextAreaValue] = useState('');

  const isBatchMode = batchValues.length >= 2;

  useEffect(() => {
    const parsedValues = parseBatchSearchValues(value);
    if (parsedValues.length >= 2) {
      setBatchValues((currentValues) => (
        sameValues(currentValues, parsedValues) ? currentValues : parsedValues
      ));
    } else {
      setBatchValues([]);
    }
  }, [value]);

  const getCurrentValues = () => {
    if (batchValues.length > 0) return [...batchValues];
    return parseBatchSearchValues(value);
  };

  const emitValues = (values) => {
    const uniqueValues = [...new Set(values)].filter(Boolean);
    setBatchValues(uniqueValues.length >= 2 ? uniqueValues : []);
    onChange?.(serializeValues(uniqueValues));
    return uniqueValues;
  };

  const searchAfterChange = () => {
    // Ant Design Form 在 onChange 后才会更新字段值，需要等一个 tick 再提交。
    setTimeout(() => onSearch?.(), 0);
  };

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    const parsedValues = parseBatchSearchValues(nextValue);
    if (hasMultipleBatchValues(nextValue)) {
      emitValues(parsedValues);
      return;
    }

    setBatchValues([]);
    onChange?.(nextValue);
  };

  const handlePaste = (event) => {
    const pastedText = event.clipboardData?.getData('text') || '';
    const pastedValues = parseBatchSearchValues(pastedText);
    if (!hasMultipleBatchValues(pastedText)) return;

    event.preventDefault();
    emitValues([...getCurrentValues(), ...pastedValues]);
  };

  const handleRemoveValue = (index) => {
    emitValues(batchValues.filter((_, valueIndex) => valueIndex !== index));
  };

  const handleClear = () => {
    setBatchValues([]);
    setTextAreaValue('');
    setPopoverVisible(false);
    onChange?.('');
    searchAfterChange();
  };

  const handleConfirm = () => {
    emitValues([
      ...getCurrentValues(),
      ...parseBatchSearchValues(textAreaValue),
    ]);
    setTextAreaValue('');
    setPopoverVisible(false);
    searchAfterChange();
  };

  const handleClose = () => {
    setPopoverVisible(false);
    setTextAreaValue('');
  };

  const popoverContent = (
    <div style={{ width: 380 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          paddingBottom: 8,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          {`批量搜索${label}`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {batchValues.length > 0 && (
            <span style={{ color: '#1890ff', fontSize: 13 }}>
              {`已选择 ${batchValues.length} 个`}
            </span>
          )}
          <CloseOutlined
            aria-label="关闭批量查询"
            style={{ color: 'rgba(0,0,0,0.45)', cursor: 'pointer', fontSize: 12 }}
            onClick={handleClose}
          />
        </div>
      </div>

      {batchValues.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>
            {`当前已选择的${label}`}
          </div>
          <div
            style={{
              maxHeight: 140,
              overflowY: 'auto',
              padding: '8px 6px 4px 8px',
              background: '#fafafa',
              borderRadius: 6,
              border: '1px solid #f0f0f0',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px 4px',
            }}
          >
            {batchValues.map((batchValue, index) => (
              <Tag
                key={batchValue}
                closable
                color="blue"
                onClose={(event) => {
                  event.preventDefault();
                  handleRemoveValue(index);
                }}
                style={{
                  margin: 0,
                  padding: '2px 8px',
                  fontSize: 13,
                  borderRadius: 4,
                }}
              >
                {batchValue}
              </Tag>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 8, color: '#999', fontSize: 12 }}>
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          <li>{`每行输入一个${label}，按回车键换行`}</li>
          <li>支持从 Excel 复制一列后直接粘贴</li>
        </ul>
      </div>

      <Input.TextArea
        aria-label={`批量输入${label}`}
        value={textAreaValue}
        onChange={(event) => setTextAreaValue(event.target.value)}
        placeholder={`粘贴${label}，每行一个`}
        autoSize={{ minRows: 3, maxRows: 6 }}
        autoFocus
        style={{ marginBottom: 8 }}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button size="small" onClick={handleClear}>清空</Button>
        <Button size="small" type="primary" onClick={handleConfirm}>确认</Button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', width: '100%', ...style }}>
      {isBatchMode ? (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            height: 32,
            border: '1px solid #d9d9d9',
            borderRadius: '6px 0 0 6px',
            borderRight: 'none',
            padding: '0 8px 0 11px',
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
          }}
        >
          <span
            style={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'rgba(0,0,0,0.88)',
              fontSize: 14,
            }}
          >
            {`${batchValues[0]} 等 ${batchValues.length} 个`}
          </span>
          <CloseCircleFilled
            aria-label="清除批量查询"
            style={{
              color: 'rgba(0,0,0,0.25)',
              cursor: 'pointer',
              fontSize: 12,
              marginLeft: 4,
            }}
            onClick={handleClear}
          />
        </div>
      ) : (
        <Input
          value={value || ''}
          onChange={handleInputChange}
          onPaste={handlePaste}
          placeholder={placeholder}
          allowClear
          style={{
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: 'none',
          }}
        />
      )}
      <Popover
        visible={popoverVisible}
        trigger={[]}
        content={popoverContent}
        placement="bottomRight"
        destroyTooltipOnHide
        onVisibleChange={setPopoverVisible}
      >
        <Button
          style={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            whiteSpace: 'nowrap',
          }}
          onClick={() => setPopoverVisible(true)}
        >
          {`${buttonLabel || '查询多人'}${isBatchMode ? `(${batchValues.length})` : ''}`}
        </Button>
      </Popover>
    </div>
  );
};

BatchSearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  buttonLabel: PropTypes.string,
  onSearch: PropTypes.func,
  style: PropTypes.shape({}),
};

BatchSearchInput.defaultProps = {
  value: '',
  onChange: () => {},
  placeholder: '请输入',
  label: '查询条件',
  buttonLabel: '查询多人',
  onSearch: () => {},
  style: {},
};

export default BatchSearchInput;
